import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'pt';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    pt: 'Seleção'
  },
  'nav.matches': {
    en: 'Matches',
    pt: 'Jogos'
  },
  'nav.deals': {
    en: 'Deals',
    pt: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    pt: 'Resultados Ao Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    pt: 'Compre a Sua'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Brazil 2026",
    pt: 'Agora você faz parte do Brasil 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Brazil,",
    pt: 'Não Apenas Apoie o Brasil,'
  },
  'hero.title2': {
    en: 'Live It',
    pt: 'Viva Isso'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    pt: 'Atualizado em tempo real. Acesse conteúdo exclusivo, atualizações de jogos e ofertas de viagem diretamente do seu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    pt: 'Ver Jogos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    pt: 'Explorar Seleção'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    pt: 'Deslize para descobrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    pt: 'Nosso Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    pt: 'Uma Nação Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    pt: 'Na Glória'
  },
  'team.desc': {
    en: "Brazil is the most successful national team in World Cup history with 5 titles (1958, 1962, 1970, 1994, 2002). Known as the 'Seleção Canarinha', Brazil represents the beautiful game - jogo bonito.",
    pt: "O Brasil é a seleção mais bem-sucedida da história das Copas do Mundo com 5 títulos (1958, 1962, 1970, 1994, 2002). Conhecida como 'Seleção Canarinha', o Brasil representa o jogo bonito."
  },
  'team.since': {
    en: 'Since 1914',
    pt: 'Desde 1914'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    pt: 'Explorar Nossa História Completa →'
  },
  'team.stat1': {
    en: '5x World Cup',
    pt: '5x Copa do Mundo'
  },
  'team.stat1Desc': {
    en: 'Champions of the World',
    pt: 'Campeões do Mundo'
  },
  'team.stat2': {
    en: '9x Copa América',
    pt: '9x Copa América'
  },
  'team.stat2Desc': {
    en: 'South American Kings',
    pt: 'Reis da América do Sul'
  },
  'team.stat3': {
    en: '22 World Cups',
    pt: '22 Copas do Mundo'
  },
  'team.stat3Desc': {
    en: 'Global Stage Appearances',
    pt: 'Participações no Palco Mundial'
  },
  'team.stat4': {
    en: '4x Confederations',
    pt: '4x Confederações'
  },
  'team.stat4Desc': {
    en: 'FIFA Cup Winners',
    pt: 'Vencedores da Copa FIFA'
  },
  'team.videoLabel': {
    en: 'Sangue Brasileiro',
    pt: 'Sangue Brasileiro'
  },
  'team.videoTitle1': {
    en: 'One Nation, One',
    pt: 'Uma Nação, Um'
  },
  'team.videoTitle2': {
    en: 'Heartbeat',
    pt: 'Batimento'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    pt: 'O Elenco'
  },
  'players.title1': {
    en: 'The Stars of',
    pt: 'As Estrelas do'
  },
  'players.title2': {
    en: 'Brazil',
    pt: 'Brasil'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    pt: 'Ver Elenco Completo na ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    pt: 'Atacante'
  },
  'players.midfielder': {
    en: 'Midfielder',
    pt: 'Meio-campista'
  },
  'players.defender': {
    en: 'Defender',
    pt: 'Zagueiro'
  },
  'players.goalkeeper': {
    en: 'Goalkeeper',
    pt: 'Goleiro'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    pt: 'Copa do Mundo 2026'
  },
  'matches.title1': {
    en: 'Match',
    pt: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    pt: 'Jogos'
  },
  'matches.desc': {
    en: 'Follow the Seleção in Group H. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    pt: 'Acompanhe a Seleção no Grupo H. Obtenha atualizações ao vivo, escalações oficiais e resultados em tempo real do maior palco do futebol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    pt: 'Classificação do Grupo'
  },
  'matches.live': {
    en: 'Live',
    pt: 'Ao Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    pt: 'Atualiza automaticamente a cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    pt: 'Atualizar Agora'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    pt: 'Ver Classificação Ao Vivo no FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    pt: 'Próximo Jogo'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    pt: 'Calendário ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    pt: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    pt: 'FlashScore Ao Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    pt: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Brazil By The Numbers',
    pt: 'Brasil em Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    pt: 'Participações em Copas'
  },
  'stats.titles': {
    en: 'World Cup Titles',
    pt: 'Títulos Mundiais'
  },
  'stats.goals': {
    en: 'International Goals',
    pt: 'Gols Internacionais'
  },
  'stats.neymar': {
    en: 'Neymar Goals (Record)',
    pt: 'Gols de Neymar (Recorde)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    pt: 'Os Arquivos'
  },
  'history.title1': {
    en: 'Moments That',
    pt: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    pt: 'Fizeram História'
  },
  'history.wc2002': {
    en: '2002 World Cup Final Victory',
    pt: 'Vitória na Final da Copa de 2002'
  },
  'history.pele': {
    en: 'Pelé Did It First',
    pt: 'Pelé Fez Primeiro'
  },
  'history.ronaldo': {
    en: "Ronaldo's Redemption 2002",
    pt: 'A Redenção de Ronaldo em 2002'
  },
  'history.team1970': {
    en: 'Brazil 1970 - The Perfect Team',
    pt: 'Brasil 1970 - O Time Perfeito'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    pt: 'Mais Que Um Jogo'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    pt: 'Sinta a Paixão do'
  },
  'fan.title2': {
    en: 'Brazil',
    pt: 'Brasil'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    pt: 'Junte-se a milhões de vozes cantando como uma só. Vista as cores, sinta a história e faça parte da jornada para a glória mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    pt: 'Viaje para a Copa do Mundo'
  },
  'deals.title1': {
    en: 'Exclusive',
    pt: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    pt: 'para Torcedores'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Brazil fans on tickets, hotels, flights & transport to support the Seleção in North America.',
    pt: 'Descontos exclusivos para torcedores do Brasil em ingressos, hotéis, voos e transporte para apoiar a Seleção na América do Norte.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    pt: 'Desbloqueie Descontos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    pt: 'Aplique o código no checkout dos sites parceiros'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    pt: 'Ingressos para Jogos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    pt: 'Até 15% de desconto em pacotes da fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    pt: 'Encontrar Ingressos'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    pt: 'Hotéis & Acomodação'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    pt: 'Ofertas de hotéis parceiros perto dos estádios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    pt: 'Reservar Hotéis'
  },
  'deals.flights': {
    en: 'Flights',
    pt: 'Voos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from São Paulo & Rio to USA',
    pt: 'Tarifas especiais de São Paulo e Rio para os EUA'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    pt: 'Pesquisar Voos'
  },
  'deals.transport': {
    en: 'Local Transport',
    pt: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    pt: 'Serviços de transfer e aluguel de carros nas cidades dos jogos'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    pt: 'Planejar Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Brazil 2026',
    pt: 'Tudo o Que Você Precisa para o Brasil 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    pt: 'QG do'
  },
  'fanHq.title2': {
    en: 'HQ',
    pt: 'Torcedor'
  },
  'fanHq.countdown': {
    en: "Countdown to Brazil's First Match",
    pt: 'Contagem Regressiva para o Primeiro Jogo do Brasil'
  },
  'fanHq.days': {
    en: 'Days',
    pt: 'Dias'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    pt: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    pt: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    pt: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    pt: 'Capacidade'
  },
  'fanHq.chants': {
    en: 'Learn the',
    pt: 'Aprenda os'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    pt: 'Cantos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    pt: 'Hino Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    pt: 'Canto Clássico de Estádio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    pt: 'Favorito da Torcida'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    pt: 'Canto de Vitória'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    pt: 'Ver no YouTube'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    pt: 'Links'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    pt: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    pt: 'Onde Assistir'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    pt: 'Canais de streaming e TV'
  },
  'fanHq.fanZones': {
    en: 'Fan Zones',
    pt: 'Fan Zones'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA Fan Fest locations',
    pt: 'Locais oficiais do FIFA Fan Fest'
  },
  'fanHq.cbfApp': {
    en: 'CBF Official App',
    pt: 'App Oficial da CBF'
  },
  'fanHq.cbfAppDesc': {
    en: 'Download the official app',
    pt: 'Baixe o app oficial'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    pt: 'Calendário Completo da Copa'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    pt: 'Todos os 104 jogos'
  },
  // Footer
  'footer.desc': {
    en: 'The official digital hub for the Brazil National Team. Access exclusive content, real-time match data, and follow the Seleção on their journey to glory.',
    pt: 'O hub digital oficial da Seleção Brasileira. Acesse conteúdo exclusivo, dados de jogos em tempo real e acompanhe a Seleção em sua jornada para a glória.'
  },
  'footer.resources': {
    en: 'Resources',
    pt: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    pt: 'Seleção'
  },
  'footer.firstTeam': {
    en: 'First Team',
    pt: 'Time Principal'
  },
  'footer.matches': {
    en: 'Matches',
    pt: 'Jogos'
  },
  'footer.standings': {
    en: 'Standings',
    pt: 'Classificação'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    pt: 'Ofertas de Viagem'
  },
  'footer.support': {
    en: 'Support',
    pt: 'Suporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    pt: 'Fale Conosco'
  },
  'footer.faq': {
    en: 'FAQ',
    pt: 'FAQ'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    pt: 'Política de Privacidade'
  },
  'footer.terms': {
    en: 'Terms of Service',
    pt: 'Termos de Serviço'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    pt: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Confederação Brasileira de Futebol. All rights reserved.',
    pt: 'Confederação Brasileira de Futebol. Todos os direitos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'pt' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-214">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}